import { matchPath } from 'react-router-dom';

export const CLIENT_COMMAND_REGISTRY_VERSION = '6';
export const CLIENT_COMMAND_SCREEN_COUNT = 17;

export const CLIENT_COMMAND_SCOPE_TYPES = Object.freeze({
  GLOBAL: 'Global',
  CLIENT: 'Client',
  CLIENT_APPLICATION: 'ClientApplication',
});

const REMOVED_ROUTE_SEGMENTS = [
  '/client-setup',
  '/applications-environments',
  '/contacts-addresses',
  '/application-settings',
  '/data-stores',
  '/background-processing',
  '/logging-monitoring',
  '/integrations-apis',
  '/configurations$',
];

const hasText = (value) => typeof value === 'string' && value.trim().length > 0;

const hasExpectedScopeParameters = (screen) => {
  const hasClient = screen.RouteTemplate.includes(':clientId');
  const hasApplication = screen.RouteTemplate.includes(':appId');

  if (screen.ScopeType === CLIENT_COMMAND_SCOPE_TYPES.GLOBAL) {
    return !hasClient && !hasApplication;
  }

  if (screen.ScopeType === CLIENT_COMMAND_SCOPE_TYPES.CLIENT) {
    return hasClient && !hasApplication;
  }

  if (screen.ScopeType === CLIENT_COMMAND_SCOPE_TYPES.CLIENT_APPLICATION) {
    return hasClient && hasApplication;
  }

  return false;
};

export const resolveClientCommandRegistry = ({
  registryVersion,
  screens,
  currentEnvironment,
  displayEnvironment,
} = {}) => {
  if (registryVersion !== CLIENT_COMMAND_REGISTRY_VERSION) {
    return {
      status: 'error',
      error: 'The server returned an unsupported Client Command registry version.',
      screens: [],
    };
  }

  if (!Array.isArray(screens) || screens.length !== CLIENT_COMMAND_SCREEN_COUNT) {
    return {
      status: 'error',
      error: 'The server did not return the complete Client Command screen registry.',
      screens: [],
    };
  }

  if (!hasText(currentEnvironment) || !hasText(displayEnvironment)) {
    return {
      status: 'error',
      error: 'The server did not return a trusted Current Environment.',
      screens: [],
    };
  }

  const normalized = [...screens]
    .sort((left, right) => left.Order - right.Order)
    .map((screen) => ({ ...screen }));
  const keys = new Set();
  const routes = new Set();

  const hasInvalidScreen = normalized.some((screen, index) => {
    const route = hasText(screen.RouteTemplate) ? screen.RouteTemplate.trim() : '';
    const routeLower = route.toLowerCase();
    const key = hasText(screen.Key) ? screen.Key.trim() : '';
    const duplicate = keys.has(key) || routes.has(routeLower);
    keys.add(key);
    routes.add(routeLower);

    return screen.Order !== index + 1
      || !key
      || !hasText(screen.Group)
      || !hasText(screen.DisplayName)
      || !hasText(screen.Description)
      || !hasText(screen.Permission)
      || !route.startsWith('/command/')
      || duplicate
      || REMOVED_ROUTE_SEGMENTS.some((segment) => (
        segment.endsWith('$')
          ? routeLower.endsWith(segment.slice(0, -1))
          : routeLower.includes(segment)
      ))
      || !hasExpectedScopeParameters(screen)
      || screen.RequiresClient !== (
        screen.ScopeType !== CLIENT_COMMAND_SCOPE_TYPES.GLOBAL
      )
      || screen.RequiresApplication !== (
        screen.ScopeType === CLIENT_COMMAND_SCOPE_TYPES.CLIENT_APPLICATION
      )
      || typeof screen.RequiresAccess !== 'boolean'
      || typeof screen.CanAccess !== 'boolean'
      || typeof screen.CanViewOnNav !== 'boolean'
      || typeof screen.CanView !== 'boolean'
      || typeof screen.CanWrite !== 'boolean'
      || typeof screen.CanDelete !== 'boolean'
      || typeof screen.CanExecute !== 'boolean';
  });

  if (hasInvalidScreen) {
    return {
      status: 'error',
      error: 'The server returned an invalid Client Command screen descriptor.',
      screens: [],
    };
  }

  return {
    status: 'ready',
    error: null,
    screens: normalized,
    currentEnvironment: currentEnvironment.trim(),
    displayEnvironment: displayEnvironment.trim(),
  };
};

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const isClientCommandIdentifier = (value) => (
  typeof value === 'string' && GUID_PATTERN.test(value)
);

export const getClientCommandRouteScope = (screens, pathname) => {
  for (const screen of screens) {
    const match = matchPath(
      { path: screen.RouteTemplate, end: true, caseSensitive: true },
      pathname,
    );
    if (match) {
      return {
        screen,
        clientId: isClientCommandIdentifier(match.params.clientId)
          ? match.params.clientId
          : null,
        appId: isClientCommandIdentifier(match.params.appId)
          ? match.params.appId
          : null,
        hasInvalidClientId: !!match.params.clientId
          && !isClientCommandIdentifier(match.params.clientId),
        hasInvalidAppId: !!match.params.appId
          && !isClientCommandIdentifier(match.params.appId),
      };
    }
  }

  return null;
};

export const buildClientCommandRoute = (screen, { clientId, appId } = {}) => {
  if (screen.ScopeType !== CLIENT_COMMAND_SCOPE_TYPES.GLOBAL
    && !isClientCommandIdentifier(clientId)) {
    return null;
  }

  if (screen.ScopeType === CLIENT_COMMAND_SCOPE_TYPES.CLIENT_APPLICATION
    && !isClientCommandIdentifier(appId)) {
    return null;
  }

  return screen.RouteTemplate
    .replace(':clientId', clientId || '')
    .replace(':appId', appId || '');
};
